import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API } from '@configs/api.config';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { File, FileRequest } from '../models/files';
import {EntityMap} from '@shared/models/types';
import {VersionedFile} from '@control-tower/models/files/versionedFile.model';
import {FileMetadata} from '@control-tower/models/files/file-metadata.model';
import {AttachmentModel} from '../../fuel/models/attachment.model';
import {LogSheetVersion, LogSheetVersionsResponse} from '@control-tower/models/log-sheet-summary.model';

@Injectable({
  providedIn: 'root'
})
export class FileService {

  constructor(private readonly http: HttpClient) {}

  getDocuments(jobFileId: string): Observable<any> {
    const url = API.s3.getDocuments.format([jobFileId]);
    return this.http.get<any>(url);
  }

  getDocumentLink(fileRequest: FileRequest): Observable<any> {
    const url = API.youAreHere.getPDF.getDocumentLink.format([]);
    return this.http.post(url, fileRequest, {responseType: 'text'});
  }

  getJPMDocumentsMetaData(orgId: string, jobRefId: string): Observable<EntityMap<string, FileMetadata[]>> {
    const url = API.jpm.getJPMDocuments.format([orgId, jobRefId]);
    return this.http.get<EntityMap<string, FileMetadata[]>>(url);
  }

  fetchDocumentUrl(documentId: string): Observable<AttachmentModel> {
    const url = API.fileHandelar.imageUrl.format([documentId]);
    return this.http.get<AttachmentModel>(url);
  }

  /** All log sheet versions of a load, newest first (empty when the load has none yet). */
  getLogSheetsByLoad(orgId: string, jobRefId: string, loadId: string): Observable<LogSheetVersion[]> {
    const url = API.logSheet.getLogSheetsByLoad.format(this.encode([orgId, jobRefId, loadId]));
    // A load has one job report, shared by every job of a job file chain, so a version can carry another
    // tenant's org/job ids. Downloads go through the viewer's own job (the backend finds the report by load);
    // the stored ids are only a fallback when the caller has none.
    return this.http.get<LogSheetVersionsResponse>(url).pipe(
      map((response) => (response?.versions ?? []).map((version) => ({
        ...version,
        orgId: orgId || version.orgId || response.orgId,
        jobRefId: jobRefId || version.jobRefId || response.jobRefId,
        loadId: loadId || version.loadId || response.loadId,
      })))
    );
  }

  /**
   * Records a download of one version and returns it as it must be printed: badge ORIGINAL on its
   * first download, DUPLICATE afterwards, with the footer's downloadedBy / downloadedAt filled in.
   */
  downloadLogSheetVersion(orgId: string, jobRefId: string, loadId: string, version: string): Observable<LogSheetVersion> {
    const url = API.logSheet.downloadLogSheetVersion.format(this.encode([orgId, jobRefId, loadId, version]));
    return this.http.post<LogSheetVersion>(url, {});
  }

  private encode(params: string[]): string[] {
    return params.map((param) => encodeURIComponent(param ?? ''));
  }
}
